import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PackageX, Database, Hourglass, Cable } from 'lucide-react';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import { FaqSchema } from '@/components/site/Seo';
import { HERO_IMAGES } from '@/data/heroImages';
import {
  ServiceHero, ProblemsBlock, BuildBlock, ApproachBlock,
  OutcomesBlock, RelatedServices, PageLinks,
} from '@/components/site/ServicePageParts';
import { OwnProducts } from '@/pages/Landing';
import { SITE } from '@/data/site';

gsap.registerPlugin(ScrollTrigger);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROBLEMS = [
  { slug: 'off-the-shelf', icon: PackageX, title: 'Off-the-shelf tools that don’t fit', body: 'You shaped your workflow around the tool, not the other way around. Every quarter someone proposes a new SaaS that promises to fix it. It never does.' },
  { slug: 'silos', icon: Database, title: 'Data trapped in silos', body: 'CRM doesn’t talk to billing. Billing doesn’t talk to ops. You have the data—you just can’t see it in one place. Decisions take days, not hours.' },
  { slug: 'manual-ops', icon: Hourglass, title: 'Manual operations eating hours', body: 'Three people, eight hours a week, on a copy-paste task that a script could do in eleven minutes. The hours are the cost; the morale is the bill.' },
  { slug: 'no-integrations', icon: Cable, title: 'Systems that won’t talk', body: 'A patchwork of integrations held together with Zapier and prayer. We replace the prayer with a thin API layer that owns the contract.' },
];

const BUILD = [
  { slug: 'internal-tools', tag: 'Ops · Internal', title: 'Internal tools', body: 'A single console for the team—orders, customers, inventory, approvals. Replaces the spreadsheet stack and the three Notion boards.', accent: '#6C5CE7' },
  { slug: 'workflow-automation', tag: 'Automation', title: 'Workflow automation', body: 'Trigger / condition / action—but durable, observable and version-controlled. n8n / Temporal / custom workers depending on the load.', accent: '#4B9FFF' },
  { slug: 'api-layer', tag: 'Integration · API', title: 'API & integration layer', body: 'A thin platform that owns the contract between your CRM, billing, ops and warehouse. Webhooks in, idempotent writes out.', accent: '#6C5CE7' },
  { slug: 'crm-erp', tag: 'Bespoke · CRM/ERP', title: 'Tailored CRM / ERP', body: 'When Salesforce / Zoho / Odoo cost you more in customisation than a clean rebuild would. We start from your nouns, not theirs.', accent: '#4B9FFF' },
  { slug: 'data-dashboards', tag: 'Insight · BI', title: 'Data dashboards', body: 'A board-grade view of the business in one screen. Metabase / Superset / custom depending on data volume + access policy.', accent: '#4B9FFF' },
  { slug: 'legacy', tag: 'Modernisation', title: 'Legacy modernization', body: 'The 12-year-old VB / PHP / Access system that runs your business. Strangler-fig migration—replace one route at a time, never the whole thing.', accent: '#6C5CE7' },
];

const APPROACH = [
  { slug: 'deep-discovery', step: '01', title: 'Deep discovery', body: 'Two weeks. We sit beside the people doing the work, not just the people commissioning the project. We leave with a process map signed off by the floor.' },
  { slug: 'process-mapping', step: '02', title: 'Process mapping', body: 'Every step, every actor, every exception. We mark the ones to keep, the ones to automate, and the two we will gently retire.' },
  { slug: 'architecture', step: '03', title: 'Architecture', body: 'A diagram you can read aloud at a board meeting. Data flow, ownership, identity, audit. Decisions get documented—designers and engineers stay in sync.' },
  { slug: 'agile-build', step: '04', title: 'Agile build', body: 'Two-week sprints, demo to your ops lead every Friday. We deploy to staging on day one and never let it break.' },
  { slug: 'integration', step: '05', title: 'Integration', body: 'Tally / Zoho / SAP / Razorpay / WhatsApp Business / e-Invoice—we’ve done them. Sandbox first, then mirror-mode, then production.' },
  { slug: 'training', step: '06', title: 'Training & handover', body: 'A Loom + a Notion runbook + one in-person training day. Your team owns it by week one of go-live.' },
  { slug: 'support', step: '07', title: 'Support', body: 'A four-week hyper-care window, then a steady retainer. We stay through the second quarterly review, not just the launch press.' },
];

const FAQ = [
  { q: 'Will the system work with our existing stack?', a: 'Yes. We start by mapping what you already have—Tally, Zoho, SAP, GSuite, Slack, WhatsApp Business—and design integration before greenfield. We try hard to never replace what already works.' },
  { q: 'How is this different from buying SaaS?', a: 'SaaS shapes you. Bespoke shapes the system. The break-even is usually 18–30 months in our experience—but the bigger story is fit. Bespoke removes one large source of operational friction permanently.' },
  { q: 'What about training and adoption?', a: 'A Loom library + a Notion runbook + one in-person training day. We also build the system so it self-documents (audit trails, inline help) so new joiners ramp in days.' },
  { q: 'Who owns the code?', a: 'You do. We hand over a private Git org, IaC, the database, the secrets. We sign work-for-hire contracts as a default, not on request.' },
  { q: 'Can we start small?', a: 'Almost always. A 4–6 week Sprint that automates one painful workflow is the easiest way to test whether we are a good fit for the larger build.' },
  { q: 'Will it scale across regions / business units?', a: 'Multi-tenant on day one, even if you start with one. Region- and BU-aware policies are the kind of thing that is expensive to bolt on later—and cheap to do right at the start.' },
];

const OUTCOMES = [
  { kpi: 38, prefix: '+', suffix: '%', label: 'team capacity unlocked' },
  { kpi: 4200, suffix: '+', label: 'hrs / yr automated (median)' },
  { kpi: 12, suffix: ' wks', label: 'first workflow live' },
  { kpi: 0, suffix: '', label: 'systems we replaced that worked' },
];

/* ----- Signature 1: Animated architecture diagram that draws on scroll ----- */
function ArchDiagram() {
  const root = useRef(null);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !root.current) return;
    const ctx = gsap.context(() => {
      const paths = root.current.querySelectorAll('path[data-edge]');
      paths.forEach((p) => {
        const len = p.getTotalLength();
        p.style.strokeDasharray = String(len);
        p.style.strokeDashoffset = String(len);
      });
      const nodes = root.current.querySelectorAll('[data-arch-node]');
      const labels = root.current.querySelectorAll('[data-arch-label]');

      gsap.timeline({
        scrollTrigger: { trigger: root.current, start: 'top 75%', end: 'bottom 30%', scrub: 0.6 },
      })
        .fromTo(nodes, { autoAlpha: 0, scale: 0.85 }, { autoAlpha: 1, scale: 1, stagger: 0.08, ease: 'power2.out' }, 0)
        .fromTo(labels, { autoAlpha: 0, y: 6 }, { autoAlpha: 1, y: 0, stagger: 0.04, ease: 'power2.out' }, 0.05)
        .to(paths, { strokeDashoffset: 0, stagger: 0.05, ease: 'none' }, 0.15);
    }, root);
    return () => ctx.revert();
  }, []);

  // Nodes: external (left), platform (center), surface (right)
  const Node = ({ x, y, w = 130, h = 56, label, tone = 'mid', node }) => {
    const fill = tone === 'core' ? 'url(#nCore)' : tone === 'ext' ? 'url(#nExt)' : 'url(#nMid)';
    const stroke = tone === 'core' ? '#6C5CE7' : tone === 'ext' ? '#4B9FFF' : 'rgba(255,255,255,0.22)';
    return (
      <g data-arch-node={node}>
        <rect x={x} y={y} width={w} height={h} rx={10} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <text x={x + w / 2} y={y + h / 2 + 4} textAnchor="middle" fontFamily="Inter" fontWeight="600" fontSize="11" fill="#EDEDF2">
          <tspan data-arch-label>{label}</tspan>
        </text>
      </g>
    );
  };

  return (
    <div
      ref={root}
      data-testid="cs-arch-diagram"
      className="relative w-full rounded-[20px] border border-white/[0.12] bg-[#0E0E15] p-4 md:p-6 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -left-32 -top-32 h-[300px] w-[300px] rounded-full blur-[120px] pointer-events-none" style={{ background: 'rgba(75,159,255,0.25)' }} />
      <div className="absolute -right-32 -bottom-32 h-[300px] w-[300px] rounded-full blur-[120px] pointer-events-none" style={{ background: 'rgba(108,92,231,0.25)' }} />
      <svg viewBox="0 0 920 460" className="relative w-full h-auto">
        <defs>
          <linearGradient id="nCore" x1="0" x2="1">
            <stop offset="0%" stopColor="rgba(108,92,231,0.25)" />
            <stop offset="100%" stopColor="rgba(108,92,231,0.05)" />
          </linearGradient>
          <linearGradient id="nMid" x1="0" x2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
          </linearGradient>
          <linearGradient id="nExt" x1="0" x2="1">
            <stop offset="0%" stopColor="rgba(75,159,255,0.18)" />
            <stop offset="100%" stopColor="rgba(75,159,255,0.04)" />
          </linearGradient>
          <linearGradient id="edge" x1="0" x2="1">
            <stop offset="0%" stopColor="#4B9FFF" />
            <stop offset="100%" stopColor="#6C5CE7" />
          </linearGradient>
        </defs>

        {/* Sources (left col) */}
        <Node x={20}  y={30}  label="Tally"        tone="ext" node="src-1" />
        <Node x={20}  y={110} label="Razorpay"     tone="ext" node="src-2" />
        <Node x={20}  y={190} label="Shopify"      tone="ext" node="src-3" />
        <Node x={20}  y={270} label="WhatsApp Biz" tone="ext" node="src-4" />
        <Node x={20}  y={350} label="Field App"    tone="ext" node="src-5" />

        {/* Platform (center) */}
        <Node x={340} y={70}  w={240} h={60} label="Integration Layer (FastAPI)" tone="core" node="core-1" />
        <Node x={340} y={170} w={240} h={60} label="Workflow Engine (Temporal)"   tone="core" node="core-2" />
        <Node x={340} y={270} w={240} h={60} label="Domain DB (Postgres)"         tone="core" node="core-3" />
        <Node x={340} y={370} w={240} h={60} label="Audit + Auth (RBAC)"          tone="core" node="core-4" />

        {/* Surfaces (right) */}
        <Node x={760} y={30}  label="Ops Console" node="surf-1" />
        <Node x={760} y={110} label="Customer Portal" node="surf-2" />
        <Node x={760} y={190} label="Mobile App" node="surf-3" />
        <Node x={760} y={270} label="Analytics" node="surf-4" />
        <Node x={760} y={350} label="Public API" node="surf-5" />

        {/* Edges left -> core (curved) */}
        <g fill="none" stroke="url(#edge)" strokeWidth="1.5" opacity="0.9">
          <path data-edge d="M150,58  C220,58  260,100 340,100" />
          <path data-edge d="M150,138 C220,138 260,200 340,200" />
          <path data-edge d="M150,218 C220,218 260,300 340,300" />
          <path data-edge d="M150,298 C220,298 260,300 340,300" />
          <path data-edge d="M150,378 C220,378 260,400 340,400" />
        </g>

        {/* Edges core -> right */}
        <g fill="none" stroke="url(#edge)" strokeWidth="1.5" opacity="0.9">
          <path data-edge d="M580,100 C660,100 700,58  760,58" />
          <path data-edge d="M580,100 C660,100 700,138 760,138" />
          <path data-edge d="M580,200 C660,200 700,218 760,218" />
          <path data-edge d="M580,300 C660,300 700,298 760,298" />
          <path data-edge d="M580,400 C660,400 700,378 760,378" />
        </g>
      </svg>

      <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-ink-muted">
        <span>← Sources</span>
        <span>Bharati Group · Ops platform</span>
        <span>Surfaces →</span>
      </div>
    </div>
  );
}

/* ----- Signature 2: ROI calculator (real backend call) ----- */
function RoiCalculator() {
  const [people, setPeople] = useState(12);
  const [hours, setHours] = useState(8);
  const [cost, setCost] = useState(1200);
  const [auto, setAuto] = useState(70);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tools/roi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          people: Number(people),
          hours_per_week: Number(hours),
          hourly_cost: Number(cost),
          automation_pct: Number(auto),
        }),
      });
      if (!res.ok) throw new Error('roi failed');
      const data = await res.json();
      setResult(data);
    } catch {
      // Fall back to local calc so the widget still feels responsive
      const wH = people * hours * (auto / 100);
      setResult({
        weekly_hours_saved: Math.round(wH * 10) / 10,
        weekly_cost_saved: Math.round(wH * cost),
        annual_hours_saved: Math.round(wH * 52),
        annual_cost_saved: Math.round(wH * cost * 52),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    calc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

  return (
    <div
      data-testid="cs-roi-calculator"
      className="rounded-[20px] border border-white/[0.12] bg-ink-surface p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-8"
    >
      <div className="md:col-span-5">
        <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">— Hours & cost saved</div>
        <h3 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight leading-[1.15]">
          Plug in your numbers. We&apos;ll show you the back-of-envelope.
        </h3>
        <p className="mt-4 text-ink-muted leading-relaxed text-sm max-w-md">
          A rough sketch—not a quote. We&apos;ll sharpen these numbers on a discovery call.
        </p>
        <Field label="People doing the work">
          <input data-testid="roi-people" type="number" min={1} max={500} value={people} onChange={(e) => setPeople(e.target.value)} className="input" />
        </Field>
        <Field label="Hours / week / person on this task">
          <input data-testid="roi-hours" type="number" min={0} max={60} value={hours} onChange={(e) => setHours(e.target.value)} className="input" />
        </Field>
        <Field label="Fully-loaded hourly cost (₹)">
          <input data-testid="roi-cost" type="number" min={0} max={100000} value={cost} onChange={(e) => setCost(e.target.value)} className="input" />
        </Field>
        <Field label={`Automation potential — ${auto}%`}>
          <input
            data-testid="roi-auto"
            type="range" min={20} max={95} step={5} value={auto}
            onChange={(e) => setAuto(e.target.value)}
            className="w-full accent-violet"
          />
        </Field>
        <button
          type="button"
          onClick={calc}
          disabled={loading}
          data-testid="roi-submit"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-violet text-white px-5 py-2.5 text-sm hover:shadow-[0_10px_30px_-10px_rgba(108,92,231,0.6)] transition-shadow disabled:opacity-60"
        >
          {loading ? 'Calculating…' : 'Recalculate'} →
        </button>
      </div>

      <div className="md:col-span-7 grid grid-cols-2 gap-4 content-start">
        {[
          { l: 'Weekly hours saved', v: result ? `${result.weekly_hours_saved} hrs` : '—', c: '#4B9FFF' },
          { l: 'Weekly cost saved',  v: result ? inr(result.weekly_cost_saved) : '—',     c: '#6C5CE7' },
          { l: 'Annual hours saved', v: result ? `${Math.round(result.annual_hours_saved).toLocaleString('en-IN')} hrs` : '—', c: '#4B9FFF' },
          { l: 'Annual cost saved',  v: result ? inr(result.annual_cost_saved)  : '—',     c: '#6C5CE7' },
        ].map((k, i) => (
          <div
            key={k.l}
            data-testid={`roi-result-${i}`}
            className="rounded-[16px] border border-white/[0.12] bg-ink-bg p-6"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">{k.l}</div>
            <div className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight tabular-nums" style={{ color: k.c }}>
              {k.v}
            </div>
          </div>
        ))}
        <p className="col-span-2 text-xs text-ink-muted leading-relaxed mt-2">
          Assumes the workflow runs 52 weeks. Real numbers usually land within ±20% of this estimate.
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-5">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-ink-muted mb-2.5">{label}</span>
      {children}
    </label>
  );
}

export default function CustomerSolutionsDevelopment() {
  return (
    <div data-testid="custom-software-development-page">
      <FaqSchema items={FAQ} />
      <ServiceHero
        image={HERO_IMAGES['custom-software-development']}
        serviceNum="04"
        serviceTitle="Custom Software Development"
        headlineText="Custom software built around how you actually work."
        accent="work."
        description="Bespoke internal tools, workflow automation, integration layers and the long-tail of glue code that turns a business into a system. We start with your nouns—orders, partners, ledgers—not the vendor’s."
        secondaryCta={{ label: 'Try the ROI calculator', to: '#cs-roi' }}
        testIdPrefix="cs"
      />

      <ProblemsBlock
        heading="Four signs you’ve outgrown off-the-shelf."
        accentWord="off-the-shelf."
        intro="If two of these sound familiar, bespoke usually pays for itself in 12–24 months. We’ll show you the math on the next section."
        items={PROBLEMS}
        testIdPrefix="cs"
      />

      <BuildBlock
        heading="Six shapes of bespoke software."
        accentWord="bespoke software."
        intro="From a single automation that saves twelve hours a week, to a full operating system for the business."
        items={BUILD}
        testIdPrefix="cs"
      />

      <ApproachBlock
        heading="Seven stages. One operating manual."
        accentWord="operating manual."
        intro="We embed close to the floor. The discovery is in person, the demos are weekly, the handover is a runbook your team will actually use."
        steps={APPROACH}
        testIdPrefix="cs"
      />

      {/* Signature 1: animated architecture */}
      <section data-testid="cs-architecture" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-end">
            <div className="md:col-span-7">
              <SectionLabel num="05">Signature · Architecture</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                A picture of the <span className="italic text-violet">whole business</span> on one page.
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-6 md:border-l border-white/10">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-md">
                  Bharati Group — a 110-year-old trading house in Surat. Five sources, four core services, five surfaces. Scroll to draw the diagram.
                </p>
              </Reveal>
            </div>
          </div>
          <Reveal>
            <ArchDiagram />
          </Reveal>
        </div>
      </section>

      {/* Signature 2: ROI calculator */}
      <section id="cs-roi" data-testid="cs-roi" className="relative py-24 md:py-32 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-end">
            <div className="md:col-span-7">
              <SectionLabel num="06">Try it · ROI calculator</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                See if a custom workflow earns its <span className="italic text-electric">keep.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-6 md:border-l border-white/10">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-md">
                  Estimates the weekly + annual savings of automating one task. Hits a real endpoint at <code className="text-violet">/api/tools/roi</code>—no email gate.
                </p>
              </Reveal>
            </div>
          </div>
          <Reveal>
            <RoiCalculator />
          </Reveal>
        </div>
      </section>

      {/* Featured project mini block */}
      <section data-testid="cs-featured" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionLabel num="07">Featured</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              <span className="italic text-violet">Bharati Group</span> · Ops platform.
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-md">
              A 110-year-old trading house in Surat with 14 lines of business and 11 systems. We replaced none of them and built one layer that finally made them talk—orders, GRN, payments, ledger, sales.
            </p>
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="arrow-link mt-8 inline-flex items-center gap-2 text-sm text-white"
              data-testid="cs-featured-cta"
            >
              Discuss a similar build <span className="arrow">→</span>
            </a>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4 self-start">
            {[
              { k: '11', l: 'systems unified' },
              { k: '14', l: 'business units on one platform' },
              { k: '4 hrs', l: 'avg. ops review (was: a week)' },
              { k: '0', l: 'systems we ripped out' },
            ].map((it) => (
              <div key={it.l} className="rounded-[16px] border border-white/[0.12] bg-ink-bg p-6">
                <div className="text-3xl md:text-4xl font-bold tracking-tight tabular-nums text-white">{it.k}</div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-ink-muted">{it.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <OutcomesBlock
        heading="Numbers we’d sign a contract against."
        accentWord="contract"
        intro="Medians across the last 10 bespoke engagements (2023–25)."
        values={OUTCOMES}
        testIdPrefix="cs"
      />

      <section data-testid="cs-faq" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="09">FAQ</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Things we&apos;re asked <span className="italic text-violet">before</span> the call.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FAQList items={FAQ} testIdPrefix="cs-faq" />
          </div>
        </div>
      </section>

      <OwnProducts num="—" />

      <PageLinks
        heading="Software by city, and software we have shipped"
        links={[
          { to: '/software-development-company-mumbai/', label: 'Software development company in Mumbai' },
          { to: '/work/staffly/', label: 'Staffly case study' },
          { to: '/work/shiksha-crm/', label: 'Shiksha CRM case study' },
          { to: '/work/forexpips/', label: 'ForexPips client portal' },
        ]}
        testIdPrefix="cs"
      />

      <RelatedServices exclude="custom-software-development" testIdPrefix="cs" />

      <CTABand
        eyebrow="A workflow eating your week?"
        headline="Let&apos;s see if it&apos;s worth automating."
        sub="Bring the spreadsheet, the screenshot, the Slack thread. We&apos;ll tell you—honestly—whether bespoke pays back."
      />
    </div>
  );
}
